import React, { useMemo } from "react";
import { Col, Form, Input, Row, Select, } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useIntl } from "react-intl";

import { useCountries, useStates } from "services/rest/billingInfo";
import { getCountryDisplayName, isRu } from "services/region/region";
import { useWatch } from "antd/es/form/Form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";

import "./AddressBillingInfo.less";


export function AddressBillingInfo() {
  const intl = useIntl();
  const form = useFormInstance();
  const country = useWatch("country", form);

  return (
    <>
      <div className="address-billing-info__title">
        {intl.formatMessage({ id: "jmix-store.address-billing-info.title" })}
      </div>
      <Row gutter={32}>
        <Col flex="1 1 300px">
          <Form.Item
            label={intl.formatMessage({ id: "jmix-store.address-billing-info.country" })}
            name="country"
            required
            rules={[
              {
                message: intl.formatMessage({ id: "jmix-store.address-billing-info.country.rule-message" }),
                required: true,
              }
            ]}
          >
            <CountrySelect
              onSelect={() => form.resetFields(["state"])}
            />
          </Form.Item>
        </Col>
        <Col flex="1 1 300px">
          <Form.Item
            label={intl.formatMessage({ id: "jmix-store.address-billing-info.state" })}
            name="state"
          >
            <StateSelect
              country={country}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={32}>
        <Col flex="1 1 300px">
          <Form.Item
            label={intl.formatMessage({ id: "jmix-store.address-billing-info.city" })}
            name="city"
            required
            rules={[
              {
                message: intl.formatMessage({ id: "jmix-store.address-billing-info.city.rule-message" }),
                required: true,
              }
            ]}
          >
            <Input
              placeholder={intl.formatMessage({ id: "jmix-store.address-billing-info.city.placeholder" })}
            />
          </Form.Item>
        </Col>
        <Col flex="1 1 300px">
          <Form.Item
            label={intl.formatMessage({ id: "jmix-store.address-billing-info.zip" })}
            name="zip"
          >
            <Input placeholder={intl.formatMessage({ id: "jmix-store.address-billing-info.zip.placeholder" })} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={32}>
        <Col flex="1 1 300px">
          <Form.Item
            label={intl.formatMessage({ id: "jmix-store.address-billing-info.address" })}
            name="addressLine1"
            required
            rules={[
              {
                message: intl.formatMessage({ id: "jmix-store.address-billing-info.address.rule-message" }),
                required: true,
              }
            ]}
          >
            <TextArea
              rows={1}
              placeholder={intl.formatMessage({ id: "jmix-store.address-billing-info.address.placeholder" })}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}

type CountrySelectProps = {
  value?: string,
  onChange?: (value: string) => void,
  onSelect?: () => void,
}

function CountrySelect(props: CountrySelectProps) {
  const intl = useIntl();
  const { value, onChange, onSelect } = props;
  const { response: countriesResponse, loading: countriesLoading } = useCountries();

  const countryOptions = useMemo(() => {
    return countriesResponse
      ?.map((country) => {
        const label = getCountryDisplayName(country.code, country.name);
        return {
          label,
          value: country.code,
          searchText: `${label} ${country.name}`.toLowerCase(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, isRu() ? "ru" : "en"));
  }, [countriesResponse]);


  return (
    <Select
      showSearch
      onChange={onChange}
      value={value}
      placeholder={intl.formatMessage({ id: "jmix-store.address-billing-info.country.placeholder" })}
      loading={countriesLoading}
      options={countryOptions}
      filterOption={(input, option) => {
        return (option?.searchText ?? option?.label ?? "").includes(input.toLowerCase());
      }}
      onSelect={onSelect}
    />
  );
}

type StateSelectProps = {
  country: string,
  value?: string,
  disabled?: boolean,
  onChange?: (value: string) => void,
}

function StateSelect(props: StateSelectProps) {
  const intl = useIntl();
  const { country, value, onChange, disabled } = props;
  const { response, loading } = useStates({ countryCode: country }, [country]);

  const stateOptions = response && country != null
    ? response.map((state) => {
      return {
        label: state,
        value: state,
      };
    })
    : [];

  return (
    <Select
      showSearch
      value={value}
      onChange={onChange}
      placeholder={intl.formatMessage({ id: "jmix-store.address-billing-info.state.placeholder" })}
      disabled={disabled}
      loading={loading}
      options={stateOptions}
      filterOption={(input, option) => {
        return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
      }}
    />
  );
}
